import { useTheme } from '@emotion/react';
import {
  type ForwardedRef,
  forwardRef,
  type MemoExoticComponent,
  memo,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  TYPOGRAPHY_DEFAULT_ELEMENTS,
  TYPOGRAPHY_VARIANTS,
} from '../../../config/typography/constants';

export type TypographyProps = {
  /** Variant from the TYPOGRAPHY_VARIANTS enum (accessible via Typography.VARIANTS). */
  variant: string;
  /** Override the default element. */
  element?: ReactElement | string;
  className?: string;
  children?: ReactNode;
};

export const TypographyBase = forwardRef(
  (
    { variant, element = undefined, children = undefined, ...props }: TypographyProps,
    ref: ForwardedRef<HTMLElement>,
  ) => {
    const theme = useTheme();

    const TypographyComponent = element || TYPOGRAPHY_DEFAULT_ELEMENTS[variant];

    return (
      <TypographyComponent {...props} ref={ref} css={theme.typography[variant]}>
        {children}
      </TypographyComponent>
    );
  },
);

TypographyBase.displayName = 'Typography';

export const Typography: MemoExoticComponent<typeof TypographyBase> & {
  VARIANTS?: typeof TYPOGRAPHY_VARIANTS;
} = memo(TypographyBase);

Typography.VARIANTS = TYPOGRAPHY_VARIANTS;
